import { axiosService } from "@/services/AxiosService";

/**
 * @typedef {Object} Horse
 * @property {number} id - ID del cavallo
 * @property {string} name - Nome del cavallo
 * @property {string} nickname - Soprannome del cavallo (facoltativo)
 * @property {string} microchip - Codice microchip del cavallo
 * @property {string} [apa_passport] - Passaporto APA (facoltativo)
 * @property {string} [apa_passport_expiration] - Data di scadenza del passaporto APA (facoltativo)
 * @property {string} [fise_passport] - Passaporto FISE (facoltativo)
 * @property {string} [fise_passport_expiration] - Data di scadenza del passaporto FISE (facoltativo)
 * @property {string} [fei_passport] - Passaporto FEI (facoltativo)
 * @property {string} [fei_passport_expiration] - Data di scadenza del passaporto FEI (facoltativo)
 * @property {number} breed_id - ID della razza
 * @property {number} colour_id - ID del colore
 * @property {string} foaling_date - Data di nascita del cavallo
 * @property {string} [father] - Nome del padre del cavallo
 * @property {string} [mother] - Nome della madre del cavallo
 * @property {string} [grandparent] - Nome del nonno del cavallo (facoltativo)
 * @property {number} height - Altezza del cavallo in cm
 * @property {string} sex - Sesso del cavallo ("M" per maschio, "F" per femmina)
 * @property {boolean} suspended - Stato sospeso (vero o falso)
 * @property {HorseImage[]} horse_images - Lista di immagini del cavallo
 * @property {User} user - owner
 * @property {integer} owner_id - owner id
 * @property {number} [id_stable] - ID della stalla (facoltativo)
 * @property {string} created - Data di creazione
 * @property {string} modified - Data di modifica
 */

/**
 * @typedef {Object} HorseImage
 * @property {number} id - ID dell'immagine
 * @property {number} horse_id - ID del cavallo associato
 * @property {string} url - URL dell'immagine
 * @property {string} created - Data di creazione dell'immagine
 * @property {string} modified - Data di modifica dell'immagine
 */

/**
 * @typedef {Object} User
 * @property {number} id - ID dell'utente.
 * @property {string} username - Nome utente.
 * @property {string} first_name - Nome dell'utente.
 * @property {string} last_name - Cognome dell'utente.
 * @property {string} email - Indirizzo email dell'utente.
 */



/**
 * Servizio per gestire le informazioni dei cavalli.
 */
class HorsesService {

    abortController = null;

    constructor() {
        this.axiosInstance = axiosService.getInstance(); // Usa l'istanza di AxiosService
    }

    /**
     * Recupera la lista dei cavalli.
     * @returns {Promise<Horse[]>} - Una promessa che risolve con la lista di cavalli
     * @throws {Error} - Lancia un errore se la chiamata fallisce
     */
    async getHorses(page, search = '', params = {}, length = 10) {
        try {
            const response = await this.axiosInstance.post('/horses/getHorsesForMultiUser', {page, length, search, ...params});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse info');
            }

            /** @type {Horse[]} */
            const listHorses = response.data;
            
            return listHorses;
        } catch (error) {
            console.error('Error fetching horse info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    async searchHorses(query) {
        try {
            const response = await this.axiosInstance.get('/horses/get-horses', {params: { search: query }});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse info');
            }

            /** @type {Horse[]} */
            return response.data.data;
        
        } catch (error) {
            console.error('Error fetching horse info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }


    /**
     * Recupera la lista dei cavalli.
     * @returns {Promise<Horse[]>} - Una promessa che risolve con la lista di cavalli
     * @throws {Error} - Lancia un errore se la chiamata fallisce
     */
    async getCurrentUserHorses(page, search = '', params = {}, length = 10) {
        try {
            const response = await this.axiosInstance.post('/horses/getCurrentUserHorses', {page, length, search, ...params});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse info');
            }

            /** @type {Horse[]} */
            const listHorses = response.data;
            return listHorses;
        } catch (error) {
            console.error('Error fetching horse info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Horse>} - 
     * @throws {Error} - 
     */
    async getHorseById(id) {
        try {
            const response = await this.axiosInstance.post('/horses/getHorseGivenId', {horse_id: id});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse info');
            }

            /** @type {Horse[]} */
            const horse = response.data.data.horse;
            console.log('horse --> ', horse);
            return horse;
        } catch (error) {
            console.error('Error fetching horse info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @throws {Error} - 
     */
    async deleteHorseById(id) {
        try {
            const response = await this.axiosInstance.post('/horses/deleteHorseById', {horse_id: id});

            if (response.status !== 200) {
                throw new Error('Failed to delete horse info');
            }
            return true;
        } catch (error) {
            console.error('Error deleteing horse info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Horse>} 
     * @throws {Error} 
     */
    async save(data) {
        try {
            const formData = new FormData();

            // Separate File objects from horse_images and keep only those with id
            const files = [];
            const horseImagesWithId = data.horse_images.filter(image => {
                if (image.url instanceof File) {
                    files.push(image.url);
                    image.url = null;
                    return false; // Exclude this item from horse_images
                }
                return true; // Keep this item in horse_images
            });

            if(data.weights && data.weights.length > 0){
                data.weights.forEach((item, index) => {
                    formData.append(`horse_weights[${index}][weight]`, item.weight);
                    formData.append(`horse_weights[${index}][date]`, item.date);
                    delete data.weights;
                });
            }
            console.log(horseImagesWithId);

            // Append all fields in data to FormData individually
            for (const key in data) {
                if (key === 'horse_images') {
                // Append the modified horse_images to FormData as a JSON string
                formData.append('horse_images', JSON.stringify(data.horse_images));
                } else {
                // Append other fields as they are
                formData.append(key, data[key]);
                }
            }

            // Append each File object to FormData under the 'files' field
            files.forEach((file, index) => {
                formData.append(`files[${index}]`, file);
            });
            const response = await this.axiosInstance.post('/horses/save', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });

            if (response.status !== 200) {
                throw new Error('Failed to save horse data');
            }

            /** @type {Horse} */
            const horse = response.data.data.horse;
            return horse;
        } catch (error) {
            console.error('Error saving horse:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }


    /**
     * 
     * @throws {Error} - 
     */
    async getHealthOverview(horse_id) {
        try {
            const response = await this.axiosInstance.post('/horse-health/get-horse-health-overview', {horse_id});

            if (response.status !== 200) {
                throw new Error('Failed to fetch health overview info');
            }

            return response.data.data;
        } catch (error) {
            console.error('Error fetching health overview info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }


    /**
     * 
     * @returns {Promise<User[]>}
     * @throws {Error}
     */
    async getUsers(search = '') {
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        try {
            const response = await this.axiosInstance.post('/horses/get-users-for-owner-id-field', {search}, {signal});

            if (response.status !== 200) {
                throw new Error('Failed to fetch users info');
            }

            /** @type {User[]} */
            return response.data.data;
        } catch (error) {
            console.error('Error fetching users info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    

    /**
     *
     * @returns {Promise<Horse[]>}
     */
    async getOwnedHorsesByUser() {
        try {
            const response = await this.axiosInstance.post('/users/getOwnedHorses');

            if (response.status !== 200) {
                throw new Error('Failed to fetch user info');
            }

            /** @type {Horse[]} */
            const listHorses = response.data.data.horses;
            return listHorses;
        } catch (error) {
            console.error('Error fetching user info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     *
     * @returns {Promise<Horse>}
     */
    async findMicrochip(microchip) {
        try {
            const response = await this.axiosInstance.post('/horses/microchip-search', {microchip});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse info');
            }

            /** @type {Horse} */
            return response.data.data;
        } catch (error) {
            console.error('Error fetching horse info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    async followRequest(horse_id) {
        try {
            const response = await this.axiosInstance.post('/horses/send-follow-request', {horse_id});

            if (response.status !== 200) {
                throw new Error('Failed to send follow request');
            }

            /** @type {Horse} */
            return response.data.data;
        } catch (error) {
            console.error('Error sending follow request:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    async updateFollowRequest(request) {
        try {
            const response = await this.axiosInstance.post('/horses/update-follow-request', {...request});

            if (response.status !== 200) {
                throw new Error('Failed to update follow request');
            }
            return response.data.data;
        } catch (error) {
            console.error('Error updating follow request:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

}

export const horsesService = new HorsesService();
